// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { config } from '@hooks/other/useConfig';
import React, { useCallback, useRef, useState } from 'react';
import {
  createACSLogsEnabledTransform,
  createLocalStorageAccessHelper,
} from '../utils/localStorageAccessHelper';
import {
  LocalStorageContext,
  LocalStorageState,
  localStorageStateParser,
} from './LocalStorageContext';

interface LocalStorageProviderProps {
  children: React.ReactNode;
}

export const LocalStorageProvider: React.FC<LocalStorageProviderProps> = ({ children }) => {
  const localStorageAccessHelper = useRef(
    createLocalStorageAccessHelper([createACSLogsEnabledTransform(config.acs.configVersion)]),
  );

  const [storage, setStorage] = useState<LocalStorageState>(() => {
    const rawState = localStorageAccessHelper.current.readAll();
    return localStorageStateParser(config).parse(rawState);
  });

  const setLocalStorageState = useCallback(
    <K extends keyof LocalStorageState>(key: K, value: LocalStorageState[K]): void => {
      localStorageAccessHelper.current.write(key, value);

      setStorage((current) => {
        return {
          ...current,
          [key]: value,
        };
      });
    },
    [],
  );

  const value = React.useMemo(
    () => ({
      localStorageState: storage,
      setLocalStorageState,
    }),
    [setLocalStorageState, storage],
  );

  return <LocalStorageContext.Provider value={value}>{children}</LocalStorageContext.Provider>;
};
